import type { Head } from '@unhead/schema'
import type { ComputedRef } from 'vue'
import type {
  EventListPageType,
  StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import type { AlgoliaEventItem } from '~/composables/types/algolia'
import capitalizeFirstLetter from '~/utils/capitilizeFirstLetter'

const useTransactionalPageHead = (
  type: EventListPageType,
  eventListPageObj: StrapiEventListPage,
  { script }: { script?: Head['script'] },
  {
    hits = ref([]),
    shouldBeIndexed: shouldBeIndexedOverride,
  }: {
    location?: StrapiEventListPage
    hits: ComputedRef<AlgoliaEventItem[]> | Ref<AlgoliaEventItem[]>
    /** When set, skips the default >= 5 hits gate (e.g. kits category pages). */
    shouldBeIndexed?: boolean
  }
) => {
  const route = useRoute()
  const pageQuery = route.query?.page
  const {
    public: { baseUrl },
  } = useRuntimeConfig()

  const eventCount = hits.value.length

  const { t, te, locale } = useI18n()

  definePageMeta({
    // Need to set that for translations
    title: `transactional-${type.toLowerCase()}`,
    hasCustomHreflang: true,
  })

  // * What?
  // In here we need to manually reset hreflang links
  // corresponding to the localizations of the event list pages from strapi
  //
  // * Why?
  // If we don't do so, hreflang will be setted with the same permalink for all
  // availables locales, which will most probably throw a 301 redirect because
  // permalinks vary by localization
  const linkTags =
    strapiEventListPageGetters.getLocalizations(eventListPageObj)?.map((x) => {
      const locale = strapiEventListPageGetters.getLocale(x).toLowerCase()

      const language = locale.split('-')[0]
      const path = strapiEventListPageGetters.getKonfettiPagePath(x)

      return {
        key: `i18n-alt-${language}`, // makes sure we don't have duplicate tags
        rel: 'alternate',
        hreflang: language,
        href: `${baseUrl}/${locale}${path}/`,
      }
    }) || []

  const xDefaultPath = strapiEventListPageGetters.getKonfettiPagePath(
    strapiEventListPageGetters.getLocalizationByLocale(
      eventListPageObj,
      'de-de'
    ) ?? eventListPageObj
  )

  linkTags.push({
    key: 'i18n-xd',
    hreflang: 'x-default',
    rel: 'alternate',
    href: `${baseUrl}/de-de${xDefaultPath}/`,
  })

  const pageSlug =
    route.params?.location?.toString() ||
    route.params?.category?.toString() ||
    route.params?.slug?.toString() ||
    undefined

  const capitalizedPageSlug = pageSlug
    ? pageSlug
        .split('-')
        .map((x) => capitalizeFirstLetter(x, locale.value))
        .join(' ')
        .trim()
    : null

  const pageTitle = capitalizedPageSlug || null

  const translationPayload = {
    location:
      strapiEventListPageGetters.getTitle(eventListPageObj) || pageTitle,
    category:
      strapiEventListPageGetters.getTitle(eventListPageObj) || pageTitle,
    title: pageTitle,
    eventCount,
  }

  const headTitle =
    strapiEventListPageGetters.getSeoTitle(eventListPageObj) ||
    t(
      `general.titles.event-list-page.${type.toLowerCase()}`,
      translationPayload
    )

  const description =
    strapiEventListPageGetters.getSeoMetaDescription(eventListPageObj) ||
    t(
      `general.descriptions.event-list-page.${type.toLowerCase()}`,
      translationPayload
    )
  const shouldBeIndexComputed = computed(
    () => shouldBeIndexedOverride ?? (hits.value.length || 0) >= 5
  )

  const options = computed(() => ({
    shouldBeIndexed: shouldBeIndexComputed.value,
    disableCanonicalLink: Boolean(pageQuery) && Number(pageQuery) !== 1,
    link: linkTags,
    hasCustomHreflang: true,
    head: {
      ...(headTitle && { title: headTitle }),
      titleTemplate: '%s | ' + t('general.titles.base'),
      script,
    },
    meta: {
      title: headTitle + ' | ' + t('general.titles.base'),
      'og:title': headTitle + ' | ' + t('general.titles.base'),
      description,
      'og:description': description,
      ...((strapiEventListPageGetters.getSeoImageUrl(eventListPageObj) ||
        te(
          `general.openGraphImages.event-list-page.${type.toLowerCase()}`
        )) && {
        'og:image':
          strapiEventListPageGetters.getSeoImageUrl(eventListPageObj) ||
          t(`general.openGraphImages.event-list-page.${type.toLowerCase()}`),
      }),
    },
  }))

  useSeoHelper(options)
}

export default useTransactionalPageHead
